"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.SetSeatsModal = void 0;
const react_1 = __importDefault(require("react"));
const react_hook_form_1 = require("react-hook-form");
const ui_1 = require("@app/component/ui");
const forms_1 = require("@app/component/ui/forms");
const util_1 = require("@app/util");
const use_modal_1 = require("@app/util/modal/use-modal");
const tiers_hooks_1 = require("@app/util/hooks/tiers-hooks");
const useWorkspace_1 = require("@app/util/hooks/useWorkspace");
const store_1 = require("@app/store");
const load_workspace_detail_1 = __importDefault(require("@app/ac/load-workspace-detail"));
const organization_1 = require("@app/ac/organization");
const use_set_seats_1 = require("./util/use-set-seats");
require("./set-seats-modal.scss");
/**
 * Sets the organization's TOTAL licence count. Adding licences charges the rest of the period
 * right away; removing them takes effect immediately with no refund, which the modal warns
 * about before saving.
 */
const SetSeatsModal = () => {
    const { closeModal } = (0, use_modal_1.useModal)();
    const dispatch = (0, store_1.useRootDispatch)();
    const organization = (0, tiers_hooks_1.useOrganization)();
    const workspace = (0, useWorkspace_1.useWorkspace)();
    const currentSeats = organization?.seatLimit ?? 0;
    const employeeCount = organization?.employeeCount ?? 0;
    // the server refuses fewer licences than employees, and zero in any case
    const minSeats = Math.max(employeeCount, 1);
    const { control, setValue, handleSubmit, formState } = (0, react_hook_form_1.useForm)({
        mode: 'onChange',
        defaultValues: { seats: currentSeats },
    });
    const seats = (0, react_hook_form_1.useWatch)({ control, name: 'seats' });
    const isChanged = (0, util_1.isValue)(seats) && seats !== currentSeats;
    const { preview, isPreviewLoading, isSaving, error, save } = (0, use_set_seats_1.useSetSeats)({
        organizationId: organization?.id,
        seats,
        isValid: formState.isValid && isChanged,
    });
    // licences added (positive) or removed (negative) by saving
    const change = isChanged ? seats - currentSeats : 0;
    // nothing is saved before its price is on screen
    const canSave = formState.isValid && isChanged && (0, util_1.isValue)(preview) && !isPreviewLoading;
    const step = (by) => {
        const next = Math.max((seats ?? currentSeats) + by, minSeats);
        setValue('seats', next, { shouldValidate: true });
    };
    const onSubmit = handleSubmit(async () => {
        const isSaved = await save();
        if (!isSaved)
            return;
        if (util_1.miscUtil.isDayswapsProInterface()) {
            await dispatch((0, organization_1.getOrganization)(false));
        }
        if ((0, util_1.isValue)(workspace?.id)) {
            await dispatch((0, load_workspace_detail_1.default)(workspace.id, true));
        }
        closeModal();
    });
    return (react_1.default.createElement(ui_1.Modal, { size: ui_1.Modal.SIZES.S, displayCloseButton: false, noClose: isSaving, testid: 'set-seats-modal', headerContent: react_1.default.createElement("div", { className: 'ds-title' }, (0, util_1.t)('NUMBER_OF_SEATS')), footerContent: react_1.default.createElement(react_1.default.Fragment, null,
            react_1.default.createElement(ui_1.Button, { bold: true, size: ui_1.Button.SIZES.LARGE, label: (0, util_1.t)('CANCEL'), disabled: isSaving, onClick: closeModal }),
            react_1.default.createElement(ui_1.Button, { bold: true, size: ui_1.Button.SIZES.LARGE, style: ui_1.Button.STYLES.CONTAINED, testid: 'set-seats-save-button', disabled: !canSave || isSaving, loading: isSaving || isPreviewLoading, label: (0, util_1.t)('SAVE'), onClick: onSubmit })) },
        react_1.default.createElement("div", { className: 'ds-set-seats' },
            react_1.default.createElement(ui_1.Text, { type: ui_1.Text.TYPES.BODY_MEDIUM, color: ui_1.Text.COLORS.GRAY }, (0, util_1.t)('INVOICING.seatsModalCurrent', { seats: currentSeats, employees: employeeCount })),
            react_1.default.createElement(ui_1.Spacing, { size: ui_1.Spacing.SIZES.SIZE_16 }),
            react_1.default.createElement("div", { className: 'ds-set-seats-stepper' },
                react_1.default.createElement(ui_1.Button, { ico: 'minus', size: ui_1.Button.SIZES.LARGE, testid: 'set-seats-minus-button', disabled: isSaving || !(0, util_1.isValue)(seats) || seats <= minSeats, onClick: () => step(-1) }),
                react_1.default.createElement("div", { className: 'ds-set-seats-input' },
                    react_1.default.createElement(forms_1.NumberInput, { control: control, name: 'seats', integerOnly: true, textAlignCenter: true, testid: 'set-seats-input', disabled: isSaving, rules: {
                            required: true,
                            min: {
                                value: minSeats,
                                message: (0, util_1.t)('INVOICING.seatsErrorBelowEmployees'),
                            },
                            validate: (value) => Number.isInteger(value),
                        } })),
                react_1.default.createElement(ui_1.Button, { ico: 'plus', size: ui_1.Button.SIZES.LARGE, testid: 'set-seats-plus-button', disabled: isSaving, onClick: () => step(1) })),
            react_1.default.createElement(ui_1.Spacing, { size: ui_1.Spacing.SIZES.SIZE_16 }),
            react_1.default.createElement("div", { className: 'ds-set-seats-summary' },
                change !== 0 && formState.isValid && (react_1.default.createElement(ui_1.Text, { type: ui_1.Text.TYPES.BODY_MEDIUM, testid: 'set-seats-change' }, change > 0
                    ? (0, util_1.t)('INVOICING.seatsModalAdding', { seats: change })
                    : (0, util_1.t)('INVOICING.seatsModalRemoving', { seats: -change }))),
                (0, util_1.isValue)(preview) && preview.amountDue > 0 && (react_1.default.createElement(ui_1.Text, { type: ui_1.Text.TYPES.BODY_MEDIUM, weight: ui_1.Text.WEIGHTS.SEMI_BOLD, testid: 'set-seats-amount-due' }, (0, util_1.t)('INVOICING.seatsModalAmountDue', {
                    amount: util_1.numberUtil.localizeAsCurrency(preview.amountDue / 100, {
                        currency: preview.currency.toUpperCase(),
                        minimumFractionDigits: 2,
                    }),
                })))),
            isChanged && seats < currentSeats && (react_1.default.createElement(react_1.default.Fragment, null,
                react_1.default.createElement(ui_1.Spacing, { size: ui_1.Spacing.SIZES.SIZE_12 }),
                react_1.default.createElement("div", { "data-testid": 'set-seats-decrease-warning' },
                    react_1.default.createElement(ui_1.Alert, { type: ui_1.Alert.TYPES.WARNING, size: ui_1.Alert.SIZES.FULL_WIDTH, text: (0, util_1.t)('INVOICING.seatsModalDecreaseWarning') })))),
            (0, util_1.isValue)(error) && (react_1.default.createElement(react_1.default.Fragment, null,
                react_1.default.createElement(ui_1.Spacing, { size: ui_1.Spacing.SIZES.SIZE_12 }),
                react_1.default.createElement(ui_1.Alert, { type: ui_1.Alert.TYPES.ERROR, size: ui_1.Alert.SIZES.FULL_WIDTH, text: error }))))));
};
exports.SetSeatsModal = SetSeatsModal;
exports.default = SetSeatsModal;
